import React, { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import {
    Modal,
    Card,
    Typography,
    Avatar,
    Row,
    Col,
    Tag,
    Button,
    Input,
    Space,
    Spin,
    Statistic,
} from "antd";
import {
    CrownFilled,
    TeamOutlined,
    SearchOutlined,
    BranchesOutlined,
    ApartmentOutlined,
    ReloadOutlined,
    CloseOutlined,
} from "@ant-design/icons";
import { Building2, Layers, ChevronRight } from "lucide-react";
import Person from "../../assests/user_avatar.png";
import OrgChart from "../OrgChart";
import "../hod/TeamMembers.css"; // Import Team Members card styles

const { Title, Text } = Typography;

// Helper to get initials
const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "?";
};

// Premium mesh gradients
const placeholderGradients = [
    "radial-gradient(at 0% 0%, #0e497b 0, transparent 50%), radial-gradient(at 50% 0%, #1677ff 0, transparent 50%), radial-gradient(at 100% 0%, #002c5c 0, transparent 50%)",
    "radial-gradient(at 0% 0%, #1e3a8a 0, transparent 50%), radial-gradient(at 50% 0%, #3b82f6 0, transparent 50%), radial-gradient(at 100% 0%, #172554 0, transparent 50%)",
    "radial-gradient(at 0% 0%, #1e1b4b 0, transparent 50%), radial-gradient(at 50% 0%, #4338ca 0, transparent 50%), radial-gradient(at 100% 0%, #312e81 0, transparent 50%)",
];

const getGradient = (name = "") => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return placeholderGradients[Math.abs(hash) % placeholderGradients.length];
};

export default function OrgChartList() {
    const { authData, backUrl } = useContext(AuthContext);
    const [hods, setHods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [selectedHod, setSelectedHod] = useState(null);

    const fetchHods = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backUrl}/api/emp/employees/?is_hod=1`, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                },
            });
            setHods(res.data?.results || []);
        } catch (err) {
            console.error("HOD Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHods();
    }, [backUrl, authData?.access]);

    const filteredHods = useMemo(() => {
        if (!searchTerm.trim()) return hods;
        const q = searchTerm.toLowerCase();
        return hods.filter((item) => {
            const name = `${item.user?.first_name || ""} ${item.user?.last_name || ""}`.toLowerCase();
            const code = (item.emp_code || "").toLowerCase();
            const dept = (item.department_name || "").toLowerCase();
            const desig = (item.designation_name || "").toLowerCase();
            return name.includes(q) || code.includes(q) || dept.includes(q) || desig.includes(q);
        });
    }, [hods, searchTerm]);

    const handleOpenModal = (hod) => {
        setSelectedHod(hod);
        setOpenModal(true);
    };

    return (
        <div style={{ padding: "20px", background: "#f8fafc", minHeight: "100vh" }}>
            {/* Executive BEMSS Navy Blue Header Banner */}
            <Card
                bordered={false}
                style={{
                    borderRadius: 16,
                    background: "linear-gradient(135deg, #0e497b 0%, #1f6db8 100%)",
                    color: "#ffffff",
                    marginBottom: 24,
                    boxShadow: "0 10px 25px rgba(14, 73, 123, 0.2)",
                }}
                bodyStyle={{ padding: "28px 32px" }}
            >
                <Row align="middle" justify="space-between" gutter={[16, 16]}>
                    <Col xs={24} md={16}>
                        <Space align="center" size="middle">
                            <Avatar
                                icon={<BranchesOutlined />}
                                size={54}
                                style={{ background: "rgba(255, 255, 255, 0.2)", color: "#ffffff" }}
                            />
                            <div>
                                <Title level={2} style={{ color: "#ffffff", margin: 0, fontWeight: 800 }}>
                                    Organization Structure & Hierarchy
                                </Title>
                                <Text style={{ color: "rgba(255, 255, 255, 0.9)", fontSize: 14 }}>
                                    Explore company-wide reporting lines, department leadership, and team structures.
                                </Text>
                            </div>
                        </Space>
                    </Col>
                    <Col xs={24} md={8} style={{ textAlign: "right" }}>
                        <Space size="large">
                            <Statistic
                                title={<span style={{ color: "rgba(255, 255, 255, 0.85)" }}>Department Heads</span>}
                                value={hods.length}
                                valueStyle={{ color: "#ffffff", fontWeight: 700 }}
                                prefix={<CrownFilled style={{ color: "#fbbf24", marginRight: 8 }} />}
                            />
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* Search & Action Bar */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 24,
                    flexWrap: "wrap",
                    gap: 16,
                    background: "#ffffff",
                    padding: 16,
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.02)",
                }}
            >
                <Input
                    prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                    placeholder="Search HOD by name, code, department..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    allowClear
                    style={{ width: 360, borderRadius: 8 }}
                />
                <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchHods}
                    style={{ borderRadius: 8 }}
                >
                    Refresh Directory
                </Button>
            </div>

            {/* HOD Grid Cards - Team Members Card Style */}
            {loading ? (
                <div style={{ textAlign: "center", padding: 60 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 12, color: "#64748b" }}>Loading Department Heads...</div>
                </div>
            ) : (
                <Row gutter={[24, 24]}>
                    {filteredHods.map((item) => {
                        const firstName = item.user?.first_name || "";
                        const lastName = item.user?.last_name || "";
                        const fullName = [firstName, lastName].filter(Boolean).join(" ") || "Employee";

                        return (
                            <Col xs={24} sm={12} lg={8} xl={6} key={item.id}>
                                <div
                                    className="glass-card tl-card"
                                    onClick={() => handleOpenModal(item)}
                                    style={{ borderRadius: 20 }}
                                >
                                    <div className="photo-container">
                                        {item.photo ? (
                                            <img src={item.photo} alt={fullName} className="profile-photo" />
                                        ) : (
                                            <div
                                                className="profile-photo"
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    background: getGradient(fullName),
                                                    color: "white",
                                                    position: "relative",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        fontSize: "48px",
                                                        fontWeight: 900,
                                                        letterSpacing: "3px",
                                                        textShadow: "0 6px 14px rgba(0,0,0,0.3)",
                                                        zIndex: 1,
                                                        fontFamily: "Inter, sans-serif",
                                                    }}
                                                >
                                                    {getInitials(firstName, lastName)}
                                                </div>
                                            </div>
                                        )}

                                        <div
                                            className="status-badge-floating"
                                            style={{
                                                background: "rgba(14, 73, 123, 0.9)",
                                                color: "#ffffff",
                                                border: "none",
                                                fontWeight: 900,
                                            }}
                                        >
                                            ★ HOD
                                        </div>

                                        <div className="photo-overlay">
                                            <div className="overlay-name">
                                                {fullName}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card-info-section">
                                        <div className="designation-text" style={{ fontSize: "14px" }}>
                                            {item.designation_name || "Department Head"}
                                        </div>

                                        <div className="dept-text">
                                            <Building2 size={14} strokeWidth={2} />
                                            <span style={{ fontWeight: 500 }}>{item.department_name || "—"}</span>
                                        </div>

                                        <div className="dept-text" style={{ marginTop: 4 }}>
                                            <Layers size={14} />
                                            <span style={{ color: "#94a3b8" }}>{item.emp_code || "—"}</span>
                                        </div>
                                    </div>

                                    <div className="card-action-bar">
                                        <button className="view-profile-btn">
                                            View Department Tree
                                            <ChevronRight size={14} />
                                        </button>
                                    </div>
                                </div>
                            </Col>
                        );
                    })}
                </Row>
            )}

            {/* Focused HOD Department Tree Modal */}
            <Modal
                open={openModal}
                onCancel={() => setOpenModal(false)}
                footer={null}
                width={1400}
                centered
                destroyOnClose
                closeIcon={<CloseOutlined style={{ color: "#ffffff", fontSize: 18 }} />}
                modalRender={(modal) => (
                    <div style={{ borderRadius: 20, overflow: "hidden", boxShadow: "0 20px 50px rgba(0,0,0,0.25)" }}>
                        {modal}
                    </div>
                )}
                title={
                    <div
                        style={{
                            background: "linear-gradient(135deg, #0e497b 0%, #1f6db8 100%)",
                            padding: "16px 24px",
                            margin: "-24px -24px 16px -24px",
                            color: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                        }}
                    >
                        <Avatar
                            src={selectedHod?.photo || Person}
                            size={44}
                            style={{ border: "2px solid #ffffff", boxShadow: "0 2px 8px rgba(0,0,0,0.2)" }}
                        />
                        <div>
                            <div style={{ fontWeight: 800, fontSize: 18, color: "#ffffff", lineHeight: 1.2 }}>
                                {`${selectedHod?.user?.first_name || ""} ${selectedHod?.user?.last_name || ""}`}
                            </div>
                            <div style={{ fontSize: 13, color: "rgba(255, 255, 255, 0.85)", marginTop: 2 }}>
                                {selectedHod?.department_name || "Department"} Organizational Hierarchy
                            </div>
                        </div>
                    </div>
                }
            >
                {selectedHod && (
                    <div style={{ padding: "0 8px 8px 8px" }}>
                        <OrgChart hodId={selectedHod.id} />
                    </div>
                )}
            </Modal>
        </div>
    );
}
